import { useCallback, useEffect, useRef, useState } from "react";

import dataHistoryService from "@plannel/services/data/data-history-service";
import debug from "@plannel/utils/debug-util";

/*
 * 적재·검증 작업이 서버에서 돌고 있는지를 **서버에 물어서** 안다.
 *
 * 왜 필요한가
 *   적재/검증 API 는 동기 요청이라 브라우저가 페이지를 떠나도 서버는 끝까지 수행한다.
 *   그런데 탭은 비활성이 되면 통째로 언마운트된다(components/TabContainer.js 의 TabPanel 이
 *   `value !== index` 면 null 을 반환한다). 그래서 진행 상태를 로컬 state 로만 들고 있으면
 *   탭을 옮기는 순간 사라지고, 돌아왔을 때 "아무 일도 없는 화면" 에 낡은 숫자가 남는다.
 *   그 상태에서 버튼을 다시 누르면 서버가 "Fail - xxx is Running" 을 돌려주는데
 *   사용자는 왜 실패했는지 알 수 없다.
 *
 * 진실은 서버에 있다 — z_data_load_job 의 최신 행이 complete_flg='N' 이면 진행 중이다.
 * (BulkImportController.executeDataValidation 도 같은 값으로 중복 실행을 막는다.)
 *
 * 폴링 간격을 왜 백오프로 두나
 *   작업 시간은 대상 테이블 크기·조인·오류 건수에 비례해 늘어난다
 *   (BulkImportService.dataValidation 이 count() 를 돌리고 오류 행을 CSV 로 쓴다).
 *   데이터가 쌓이면 같은 항목도 계속 느려지고 테넌트마다 다르므로, 한 번 재보고 정한
 *   고정 간격은 곧 안 맞게 된다. 짧은 작업은 빨리 알아채고 긴 작업은 요청을 낭비하지 않도록
 *   간격을 점점 벌린다.
 *
 * 요청은 **한 틱에 하나**다. 감시 대상이 검증처럼 20개가 넘어도 이름별로 묻지 않고
 * 최근 이력 한 페이지를 받아 그 안에서 찾는다. 돌고 있는 작업은 늘 최근 행에 있다.
 */
const BACKOFF_MS = [2000, 2000, 3000, 5000, 8000, 10000];

/* 한 번에 훑는 최근 이력 수 — 검증 한 번이 항목 수만큼 행을 남기므로 넉넉히 잡는다 */
const HISTORY_PAGE_SIZE = 50;

/**
 * @param {string[]} jobTableNames 감시할 job_table_name 목록. 비어 있으면 아무것도 하지 않는다.
 * @param {Function} onSettled     진행 중이던 작업이 모두 끝난 뒤 한 번 호출된다.
 * @returns {{ runningNames: string[], running: boolean, probe: Function }}
 *          probe() 는 작업을 방금 시작했을 때처럼 즉시 다시 확인하고 싶을 때 부른다.
 */
const useJobProgress = (jobTableNames, onSettled) => {
  const [runningNames, setRunningNames] = useState([]);

  /* 콜백이 매 렌더 새로 만들어져도 폴링이 다시 시작되지 않게 ref 로 고정한다 */
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  /* 돌던 것이 끝났을 때만 알리기 위한 표식 — 처음부터 유휴였으면 아무 일도 하지 않는다 */
  const wasRunningRef = useRef(false);

  /* probe() 가 폴링을 처음부터 다시 걸 수 있도록 effect 안의 시작 함수를 담아 둔다 */
  const restartRef = useRef(null);

  /* 배열 identity 가 매 렌더 바뀌어도 내용이 같으면 폴링을 다시 걸지 않는다 */
  const namesKey = (jobTableNames ?? []).join("|");

  useEffect(() => {
    const names = namesKey ? namesKey.split("|") : [];
    if (names.length === 0) {
      setRunningNames([]);
      return undefined;
    }

    const token = { cancelled: false };
    let timer = null;

    const readRunning = () =>
      dataHistoryService.getAll({ page: 0, pageSize: HISTORY_PAGE_SIZE })
        .then(response => {
          const rows = response.data?.results ?? [];
          const watched = new Set(names);
          return [...new Set(
            rows
              .filter(row => row?.completeFlg === "N" && watched.has(row.jobTableName))
              .map(row => row.jobTableName)
          )];
        })
        .catch(error => {
          debug(error);
          /* 조회가 실패했다고 "끝났다" 로 단정하면 안 된다 — 이전 판단을 유지한다 */
          return null;
        });

    const tick = async (step) => {
      const running = await readRunning();
      if (token.cancelled) {
        return;
      }

      if (running === null) {
        /* 일시적 실패 — 간격만 벌려 다시 시도한다 */
        timer = setTimeout(() => tick(step + 1), BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)]);
        return;
      }

      if (running.length > 0) {
        wasRunningRef.current = true;
        setRunningNames(running);
        timer = setTimeout(() => tick(step + 1), BACKOFF_MS[Math.min(step, BACKOFF_MS.length - 1)]);
        return;
      }

      /*
       * 끝났다고 표시하기 **전에** 호출부의 갱신이 끝나기를 기다린다.
       *   먼저 running 을 내려 버리면, 새 숫자가 도착하기 전 1~2초 동안 화면이
       *   "작업 끝" 인데 값은 옛것인 상태가 된다 — 고쳤는데 그대로인 것처럼 보인다.
       *   onSettled 가 프로미스를 돌려주지 않으면 그대로 진행한다.
       */
      if (wasRunningRef.current) {
        wasRunningRef.current = false;
        try {
          await onSettledRef.current?.();
        } catch (error) {
          debug(error);
        }
        if (token.cancelled) {
          return;
        }
      }

      setRunningNames(running);
    };

    const restart = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      tick(0);
    };

    restartRef.current = restart;
    restart();

    return () => {
      token.cancelled = true;
      restartRef.current = null;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [namesKey]);

  /*
   * 작업을 막 시작했을 때 — 다음 백오프를 기다리지 않고 지금부터 다시 확인한다.
   * 서버가 job 행을 쓰기 전에 물어보면 놓칠 수 있는데, 그 경우에도 탭을 떠났다 돌아오면
   * 마운트 시 다시 확인하므로 진행 중인 작업은 결국 잡힌다.
   */
  const probe = useCallback(() => {
    restartRef.current?.();
  }, []);

  return { runningNames, running: runningNames.length > 0, probe };
};

export default useJobProgress;
